import { useState, useEffect, useMemo, useCallback } from 'react';
import { api, formatCurrency, API } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Calculator, Plus, Trash2, Check, X, Download, Loader2, Save, Users, Building2, Wrench, Package, Target, TrendingUp, TrendingDown, Euro } from 'lucide-react';
import { toast } from 'sonner';

const SECTIONS = ['Salaires', 'Structure', 'Exploitation', 'Divers'];
const SEC_ICONS = { Salaires: Users, Structure: Building2, Exploitation: Wrench, Divers: Package };

const DEFAULTS = {
  lemas: {
    charges: [
      { id: 's1', label: 'Devin Johann', montant: 1600, section: 'Salaires' },
      { id: 's2', label: 'Reynier Aimee', montant: 1500, section: 'Salaires' },
      { id: 's3', label: 'Carole Michel', montant: 1500, section: 'Salaires' },
      { id: 's4', label: 'Patrick Fourty', montant: 1400, section: 'Salaires' },
      { id: 's5', label: 'Laurent Lesage', montant: 1500, section: 'Salaires' },
      { id: 's6', label: 'Charges patronales', montant: 4200, section: 'Salaires' },
      { id: 's7', label: 'Gerant (Laurent Sanmartin)', montant: 2000, section: 'Salaires' },
      { id: 'st1', label: 'Loyer / SCI', montant: 2200, section: 'Structure' },
      { id: 'st2', label: 'Assurances AXA', montant: 420, section: 'Structure' },
      { id: 'e1', label: 'Electricite + Gaz', montant: 750, section: 'Exploitation' },
      { id: 'e2', label: 'Eau', montant: 120, section: 'Exploitation' },
      { id: 'e3', label: 'Telephone / Internet', montant: 80, section: 'Exploitation' },
      { id: 'e4', label: 'Comptable', montant: 350, section: 'Exploitation' },
      { id: 'e5', label: 'Abonnements divers', montant: 200, section: 'Exploitation' },
      { id: 'd1', label: 'Remboursement emprunt', montant: 1100, section: 'Divers' },
      { id: 'd2', label: 'Divers / Imprevus', montant: 300, section: 'Divers' },
    ],
    params: { ticketMoyen: 25, tauxTVA: 10, foodCostCible: 30, joursOuverture: 26, nbServices: 2 },
  },
  traiteur: {
    charges: [
      { id: 't1', label: 'Personnel traiteur', montant: 2500, section: 'Salaires' },
      { id: 't2', label: 'Charges patronales', montant: 1200, section: 'Salaires' },
      { id: 't3', label: 'Part loyer / stockage', montant: 500, section: 'Structure' },
      { id: 't4', label: 'Assurance RC Pro', montant: 150, section: 'Structure' },
      { id: 't5', label: 'Vehicule (leasing + carburant)', montant: 650, section: 'Exploitation' },
      { id: 't6', label: 'Materiel jetable / emballages', montant: 300, section: 'Exploitation' },
      { id: 't7', label: 'Divers', montant: 200, section: 'Divers' },
    ],
    params: { ticketMoyen: 35, tauxTVA: 10, foodCostCible: 28, joursOuverture: 20, nbServices: 1 },
  },
};

const BudgetMensuel = () => {
  const [activite, setActivite] = useState('lemas');
  const [charges, setCharges] = useState([]);
  const [params, setParams] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [editMontant, setEditMontant] = useState('');
  const [addingSection, setAddingSection] = useState(null);
  const [newLabel, setNewLabel] = useState('');
  const [newMontant, setNewMontant] = useState('');
  const [simCouverts, setSimCouverts] = useState(45);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/budget-mensuel/${activite}`);
      setCharges(res.data.charges || []);
      setParams(res.data.params || {});
      setDirty(false);
    } catch {
      const def = DEFAULTS[activite] || DEFAULTS.lemas;
      setCharges(def.charges);
      setParams(def.params);
    }
    setLoading(false);
  }, [activite]);

  useEffect(() => { loadData(); }, [loadData]);

  const saveData = async () => {
    setSaving(true);
    try {
      await api.put(`/budget-mensuel/${activite}`, { charges, params });
      setDirty(false);
      toast.success('Budget sauvegarde');
    } catch { toast.error('Erreur de sauvegarde'); }
    setSaving(false);
  };

  // KPIs
  const kpis = useMemo(() => {
    const totalCharges = charges.reduce((s, c) => s + (c.montant || 0), 0);
    const fcRate = (params.foodCostCible || 30) / 100;
    const tvaRate = (params.tauxTVA || 10) / 100;
    const ticket = params.ticketMoyen || 25;
    const jours = params.joursOuverture || 26;
    const services = params.nbServices || 2;
    const caHT = fcRate < 1 ? totalCharges / (1 - fcRate) : 0;
    const caTTC = caHT * (1 + tvaRate);
    const couvertsTotal = ticket > 0 ? caHT / ticket : 0;
    const couvertsJour = jours > 0 ? couvertsTotal / jours : 0;
    const couvertsService = services > 0 ? couvertsJour / services : 0;
    const salaires = charges.filter(c => c.section === 'Salaires').reduce((s, c) => s + (c.montant || 0), 0);
    const massePct = caHT > 0 ? (salaires / caHT) * 100 : 0;
    const budgetAchats = caHT * fcRate;
    return { totalCharges, caHT, caTTC, couvertsTotal, couvertsJour, couvertsService, salaires, massePct, budgetAchats };
  }, [charges, params]);

  // Simulator
  const sim = useMemo(() => {
    const ticket = params.ticketMoyen || 25;
    const jours = params.joursOuverture || 26;
    const fcRate = (params.foodCostCible || 30) / 100;
    const tvaRate = (params.tauxTVA || 10) / 100;
    const caHT = simCouverts * ticket * jours;
    const caTTC = caHT * (1 + tvaRate);
    const foodCost = caHT * fcRate;
    const resultat = caHT - kpis.totalCharges - foodCost;
    const ecartPct = kpis.caHT > 0 ? ((caHT - kpis.caHT) / kpis.caHT) * 100 : 0;
    return { caHT, caTTC, foodCost, resultat, ecartPct };
  }, [simCouverts, params, kpis]);

  // CRUD
  const startEdit = (c) => { setEditingId(c.id); setEditLabel(c.label); setEditMontant(String(c.montant)); };
  const saveEdit = () => {
    setCharges(prev => prev.map(c => c.id === editingId ? { ...c, label: editLabel, montant: parseFloat(editMontant) || 0 } : c));
    setEditingId(null); setDirty(true);
  };
  const deleteCharge = (id) => { setCharges(prev => prev.filter(c => c.id !== id)); setDirty(true); };
  const addCharge = (section) => {
    if (!newLabel.trim()) return;
    setCharges(prev => [...prev, { id: crypto.randomUUID(), label: newLabel.trim(), montant: parseFloat(newMontant) || 0, section }]);
    setAddingSection(null); setNewLabel(''); setNewMontant(''); setDirty(true);
  };
  const updateParam = (key, val) => { setParams(prev => ({ ...prev, [key]: parseFloat(val) || 0 })); setDirty(true); };

  const fcColor = (params.foodCostCible || 30) < 30 ? 'text-green-400' : (params.foodCostCible || 30) <= 35 ? 'text-orange-400' : 'text-red-400';

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[rgb(var(--lm-gold))]" /></div>;

  return (
    <div className="space-y-4 max-w-6xl mx-auto" data-testid="budget-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Calculator size={18} className="text-[rgb(var(--lm-gold))]" />
          <h1 className="text-lg font-title text-[rgb(var(--lm-text))]">Budget Mensuel</h1>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <Button size="sm" onClick={saveData} disabled={saving}
              className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90 text-xs h-8" data-testid="save-budget">
              {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
              Sauvegarder
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => window.open(`${API}/budget-mensuel/export-pdf/${activite}`, '_blank')}
            className="text-[rgb(var(--lm-text-muted))] text-xs h-8" data-testid="export-pdf">
            <Download size={14} className="mr-1" /> PDF
          </Button>
        </div>
      </div>

      {/* Activity tabs */}
      <div className="flex gap-1 p-1 rounded-lg bg-[rgb(var(--lm-surface))]" data-testid="activity-tabs">
        {[{ id: 'lemas', label: 'Le Mas' }, { id: 'traiteur', label: 'Traiteur du Mas' }].map(tab => (
          <button key={tab.id} onClick={() => { if (tab.id !== activite) { if (dirty) saveData(); setActivite(tab.id); } }}
            className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${
              activite === tab.id
                ? 'bg-[rgb(var(--lm-gold)/0.15)] text-[rgb(var(--lm-gold))] shadow-sm'
                : 'text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]'
            }`} data-testid={`tab-${tab.id}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Main grid: 2 columns on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* LEFT: Charges fixes (3/5) */}
        <div className="lg:col-span-3 space-y-3">
          <div className="lm-card p-4">
            <h2 className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-3">Charges fixes mensuelles</h2>

            {SECTIONS.map(sec => {
              const Icon = SEC_ICONS[sec];
              const items = charges.filter(c => c.section === sec);
              const subtotal = items.reduce((s, c) => s + (c.montant || 0), 0);
              return (
                <div key={sec} className="mb-4" data-testid={`section-${sec}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <Icon size={13} className="text-[rgb(var(--lm-gold))]" />
                      <span className="text-xs font-semibold text-[rgb(var(--lm-text))]">{sec}</span>
                    </div>
                    <span className="text-xs font-mono text-[rgb(var(--lm-text-muted))]">{formatCurrency(subtotal)}</span>
                  </div>
                  <div className="space-y-0.5 pl-5">
                    {items.map(c => (
                      <div key={c.id} className="group flex items-center gap-2 py-1 rounded hover:bg-[rgb(var(--lm-surface-elevated)/0.5)] px-2 -mx-2" data-testid={`charge-${c.id}`}>
                        {editingId === c.id ? (
                          <>
                            <Input value={editLabel} onChange={e => setEditLabel(e.target.value)}
                              className="h-7 text-xs flex-1 bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]"
                              onKeyDown={e => e.key === 'Enter' && saveEdit()} autoFocus data-testid="edit-label" />
                            <Input type="number" value={editMontant} onChange={e => setEditMontant(e.target.value)}
                              className="h-7 text-xs w-24 font-mono bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] text-right"
                              onKeyDown={e => e.key === 'Enter' && saveEdit()} data-testid="edit-montant" />
                            <button onClick={saveEdit} className="text-green-400 hover:text-green-300" data-testid="save-edit"><Check size={14} /></button>
                            <button onClick={() => setEditingId(null)} className="text-[rgb(var(--lm-text-muted))]"><X size={14} /></button>
                          </>
                        ) : (
                          <>
                            <span className="flex-1 text-xs text-[rgb(var(--lm-text-secondary))] cursor-pointer hover:text-[rgb(var(--lm-text))]"
                              onClick={() => startEdit(c)}>{c.label}</span>
                            <span className="text-xs font-mono text-[rgb(var(--lm-text))] cursor-pointer" onClick={() => startEdit(c)}>
                              {formatCurrency(c.montant)}
                            </span>
                            <button onClick={() => deleteCharge(c.id)}
                              className="opacity-0 group-hover:opacity-100 text-red-400/60 hover:text-red-400 transition-opacity" data-testid={`del-${c.id}`}>
                              <Trash2 size={12} />
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                    {addingSection === sec ? (
                      <div className="flex items-center gap-2 py-1 px-2 -mx-2">
                        <Input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="Libelle"
                          className="h-7 text-xs flex-1 bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]"
                          onKeyDown={e => e.key === 'Enter' && addCharge(sec)} autoFocus data-testid="new-label" />
                        <Input type="number" value={newMontant} onChange={e => setNewMontant(e.target.value)} placeholder="0"
                          className="h-7 text-xs w-24 font-mono bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] text-right"
                          onKeyDown={e => e.key === 'Enter' && addCharge(sec)} data-testid="new-montant" />
                        <button onClick={() => addCharge(sec)} className="text-green-400"><Check size={14} /></button>
                        <button onClick={() => { setAddingSection(null); setNewLabel(''); setNewMontant(''); }} className="text-[rgb(var(--lm-text-muted))]"><X size={14} /></button>
                      </div>
                    ) : (
                      <button onClick={() => setAddingSection(sec)}
                        className="flex items-center gap-1 text-[10px] text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-gold))] py-0.5 transition-colors" data-testid={`add-${sec}`}>
                        <Plus size={10} /> Ajouter
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Total */}
            <div className="border-t border-[rgb(var(--lm-border))] pt-3 mt-2 flex items-center justify-between">
              <span className="text-sm font-semibold text-[rgb(var(--lm-gold))]">Total charges fixes</span>
              <span className="text-lg font-mono font-bold text-[rgb(var(--lm-gold))]" data-testid="total-charges">{formatCurrency(kpis.totalCharges)}</span>
            </div>
          </div>
        </div>

        {/* RIGHT: Params + Results (2/5) */}
        <div className="lg:col-span-2 space-y-3">
          {/* Parametres */}
          <div className="lm-card p-4">
            <h2 className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-3">Parametres de calcul</h2>
            <div className="space-y-3">
              <ParamRow label="Ticket moyen HT" suffix=" EUR" value={params.ticketMoyen} onChange={v => updateParam('ticketMoyen', v)} testId="param-ticket" />
              <ParamRow label="Taux TVA" suffix=" %" value={params.tauxTVA} onChange={v => updateParam('tauxTVA', v)} testId="param-tva" />
              <div className="flex items-center justify-between">
                <span className="text-xs text-[rgb(var(--lm-text-secondary))]">Food cost cible</span>
                <div className="flex items-center gap-1">
                  <Input type="number" value={params.foodCostCible ?? ''} onChange={e => updateParam('foodCostCible', e.target.value)}
                    className={`h-7 w-16 text-xs font-mono text-right bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] ${fcColor}`} data-testid="param-foodcost" />
                  <span className={`text-xs ${fcColor}`}>%</span>
                </div>
              </div>
              <ParamRow label="Jours d'ouverture" value={params.joursOuverture} onChange={v => updateParam('joursOuverture', v)} testId="param-jours" />
              <ParamRow label="Services / jour" value={params.nbServices} onChange={v => updateParam('nbServices', v)} testId="param-services" />
            </div>
          </div>

          {/* Resultats */}
          <div className="lm-card p-4">
            <h2 className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-3">Seuil de rentabilite</h2>
            <div className="space-y-2.5">
              <KpiRow label="CA TTC minimum" value={formatCurrency(kpis.caTTC)} icon={Euro} highlight testId="kpi-ca-ttc" />
              <KpiRow label="CA HT correspondant" value={formatCurrency(kpis.caHT)} icon={Euro} testId="kpi-ca-ht" />
              <div className="border-t border-[rgb(var(--lm-border))] my-1" />
              <KpiRow label="Couverts / jour" value={kpis.couvertsJour.toFixed(1)} icon={Target} highlight testId="kpi-couverts-jour" />
              <KpiRow label="Couverts / service" value={kpis.couvertsService.toFixed(1)} icon={Target} testId="kpi-couverts-service" />
              <div className="border-t border-[rgb(var(--lm-border))] my-1" />
              <KpiRow label="Masse salariale" value={`${formatCurrency(kpis.salaires)} (${kpis.massePct.toFixed(1)}%)`} icon={Users} testId="kpi-masse" />
              <KpiRow label="Budget achats max" value={formatCurrency(kpis.budgetAchats)} icon={Package} testId="kpi-achats" />
            </div>
          </div>
        </div>
      </div>

      {/* Simulator */}
      <div className="lm-card p-5 border border-[rgb(var(--lm-gold)/0.15)]" data-testid="simulator">
        <h2 className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-4">
          Simulateur &laquo; Et si... &raquo;
        </h2>
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-5">
          <span className="text-sm text-[rgb(var(--lm-text-secondary))] shrink-0">Si je fais en moyenne</span>
          <div className="flex items-center gap-3 flex-1">
            <input type="range" min={5} max={120} value={simCouverts} onChange={e => setSimCouverts(parseInt(e.target.value))}
              className="flex-1 h-1.5 accent-[rgb(var(--lm-gold))] bg-[rgb(var(--lm-surface-elevated))] rounded-full appearance-none cursor-pointer
                [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[rgb(var(--lm-gold))] [&::-webkit-slider-thumb]:shadow-lg"
              data-testid="sim-slider" />
            <Input type="number" value={simCouverts} onChange={e => setSimCouverts(parseInt(e.target.value) || 0)}
              className="h-8 w-16 text-center text-sm font-mono bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] text-[rgb(var(--lm-gold))]" data-testid="sim-input" />
          </div>
          <span className="text-sm text-[rgb(var(--lm-text-secondary))] shrink-0">couverts/jour</span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SimCard label="CA projete TTC" value={formatCurrency(sim.caTTC)} testId="sim-ca" />
          <SimCard label="Food cost projete" value={formatCurrency(sim.foodCost)} testId="sim-fc" />
          <div className={`rounded-lg p-3 text-center ${sim.resultat >= 0 ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
            <p className="text-[9px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-1">Resultat net</p>
            <div className="flex items-center justify-center gap-1">
              {sim.resultat >= 0 ? <TrendingUp size={14} className="text-green-400" /> : <TrendingDown size={14} className="text-red-400" />}
              <p className={`font-mono text-lg font-bold ${sim.resultat >= 0 ? 'text-green-400' : 'text-red-400'}`} data-testid="sim-resultat">
                {sim.resultat >= 0 ? '+' : ''}{formatCurrency(sim.resultat)}
              </p>
            </div>
          </div>
          <div className={`rounded-lg p-3 text-center ${sim.ecartPct >= 0 ? 'bg-green-500/5 border border-green-500/10' : 'bg-red-500/5 border border-red-500/10'}`}>
            <p className="text-[9px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-1">Ecart vs seuil</p>
            <p className={`font-mono text-lg font-bold ${sim.ecartPct >= 0 ? 'text-green-400' : 'text-red-400'}`} data-testid="sim-ecart">
              {sim.ecartPct >= 0 ? '+' : ''}{sim.ecartPct.toFixed(1)}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Sub-components
const ParamRow = ({ label, suffix, value, onChange, testId }) => (
  <div className="flex items-center justify-between">
    <span className="text-xs text-[rgb(var(--lm-text-secondary))]">{label}</span>
    <div className="flex items-center gap-1">
      <Input type="number" value={value ?? ''} onChange={e => onChange(e.target.value)}
        className="h-7 w-16 text-xs font-mono text-right bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid={testId} />
      {suffix && <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">{suffix}</span>}
    </div>
  </div>
);

const KpiRow = ({ label, value, icon: Icon, highlight, testId }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2">
      <Icon size={12} className={highlight ? 'text-[rgb(var(--lm-gold))]' : 'text-[rgb(var(--lm-text-muted))]'} />
      <span className="text-xs text-[rgb(var(--lm-text-secondary))]">{label}</span>
    </div>
    <span className={`text-xs font-mono ${highlight ? 'font-semibold text-[rgb(var(--lm-gold))]' : 'text-[rgb(var(--lm-text))]'}`} data-testid={testId}>
      {value}
    </span>
  </div>
);

const SimCard = ({ label, value, testId }) => (
  <div className="lm-card p-3 text-center">
    <p className="text-[9px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-1">{label}</p>
    <p className="font-mono text-base text-[rgb(var(--lm-text))]" data-testid={testId}>{value}</p>
  </div>
);

export default BudgetMensuel;
